<script setup lang="ts">
import { type Component, defineAsyncComponent, shallowRef, watch } from "vue";

import { useSearchStore } from "@/stores";

const searchStore = useSearchStore();

type Language = "da" | "de" | "en" | "es" | "fr";
type EasterEgg =
  | "baby"
  | "bird"
  | "confetti"
  | "dannebrog"
  | "dog"
  | "film"
  | "heart"
  | "meatball"
  | "snake"
  | "superhero";

const easterEggTriggerMap: Record<EasterEgg, Partial<Record<Language, string[]>>> = {
  baby: {
    da: ["baby", "barn", "hoppe", "nyfødt", "spædbarn"],
    de: ["Baby", "geboren", "Geburt", "Kind", "Kleinkind", "Säugling"],
    en: ["baby", "birth", "born", "bounce", "child", "infant", "swaddle"],
    es: ["bebé", "niña", "niño"],
    fr: ["bébé", "bondir", "enfant", "naissance", "né", "sauter"]
  },
  bird: {
    da: ["kvidre", "pippe", "solsort", "stær"],
    de: ["Amsel", "piepen", "Vogel", "zwitschern"],
    en: ["bird", "blackbird", "perch", "starling"],
    es: ["ave", "pájaro"],
    fr: ["oiseau", "pépier"]
  },
  confetti: {
    da: ["fejre", "fejring", "fest", "feste", "hurra", "nytår"],
    de: ["feiern", "Fest", "Konfetti", "Neujahr", "Party", "Silvester"],
    en: [
      "celebrate",
      "celebration",
      "happy New Year",
      "hooray",
      "hurrah",
      "New Year",
      "party",
      "whoop"
    ],
    fr: ["anniversaire", "célébrer", "fête"]
  },
  dannebrog: {
    da: ["Danmark", "dansk", "flag", "flage", "fødselsdag", "tillykke", "vifte"],
    de: ["Dänemark", "Dänisch", "Fahne", "Flagge", "Geburtstag", "Hurra", "jubeln"],
    en: ["birthday", "cheer", "congratulations", "Danish", "Denmark"],
    es: ["bandera"],
    fr: ["célébration", "Danemark", "Danois", "drapeau", "encourager", "félicitations"]
  },
  dog: {
    da: ["bjæffe", "gø", "hund", "hvalp", "kæledyr", "køter", "logre", "vov", "vovse"],
    de: ["bellen", "Haustier", "Hund", "Hündchen", "wedeln", "Welpe"],
    en: ["bark", "canine", "dog", "doggy", "pet", "puppy", "wag"],
    es: ["perro"],
    fr: ["aboyer", "animal", "chien", "chienne"]
  },
  film: {
    da: ["biograf", "film", "filmlærred", "projektor", "tændstikfigur", "tændstikmand", "video"],
    de: [
      "Animation",
      "Film",
      "Kamera",
      "Kino",
      "Projektion",
      "Strichmännchen",
      "Video",
      "Zeichentrickfilm"
    ],
    en: [
      "animation",
      "camera",
      "cartoon",
      "cinema",
      "film",
      "Hollywood",
      "movie",
      "projector",
      "screen",
      "stick figure"
    ],
    es: ["cine", "peli", "película"],
    fr: ["animation", "cinéma", "film", "vidéo"]
  },
  heart: {
    da: ["forelskelse", "forelsket", "hjerte", "kæreste", "kærlighed", "rød"],
    de: [
      "Beziehung",
      "Freund",
      "Freundin",
      "Herz",
      "Herzklopfen",
      "Herzrasen",
      "Liebe",
      "lieben",
      "rot",
      "verliebt",
      "Verliebtheit"
    ],
    en: [
      "alive",
      "cardiac massage",
      "crush",
      "fling",
      "flirt",
      "heart",
      "heart massage",
      "life",
      "love",
      "swoon"
    ],
    es: ["amor"],
    fr: ["aimer", "amour", "amoureux", "cœur"]
  },
  meatball: {
    da: ["bøf", "frikadelle", "hakkebøf"],
    de: ["Bulette", "Fleischkloß", "Frikadelle"],
    en: ["hamburger", "meatball", "patty"],
    fr: ["farce"]
  },
  snake: {
    da: ["krybdyr", "reptil", "slange", "sno"],
    de: ["kriechen", "Kriechtier", "Reptil", "Schlange", "schlängeln"],
    en: ["adder", "asp ", "boa", "cobra", "python", "reptile", "serpent", "slither", "snake"],
    es: ["serpiente"],
    fr: ["langue", "reptile", "serpent", "serpenter"]
  },
  superhero: {
    da: ["flyve", "helt", "superhelt", "superman", "svæve"],
    de: ["fliegen", "Superheld", "Superheldin", "superman"],
    en: ["float", "fly", "hero", "heroic", "soar", "superman"],
    es: ["superman"],
    fr: ["héro", "héroïne", "super-héro", "super-héroïne", "superman", "voler"]
  }
};

const easterEggComponentMap: Record<EasterEgg, Component> = {
  baby: defineAsyncComponent(() => import("@/components/easterEggs/TheBaby.vue")),
  bird: defineAsyncComponent(() => import("@/components/easterEggs/TheBird.vue")),
  confetti: defineAsyncComponent(() => import("@/components/easterEggs/TheConfetti.vue")),
  dannebrog: defineAsyncComponent(() => import("@/components/easterEggs/TheDannebrog.vue")),
  dog: defineAsyncComponent(() => import("@/components/easterEggs/TheDog.vue")),
  film: defineAsyncComponent(() => import("@/components/easterEggs/TheFilm.vue")),
  heart: defineAsyncComponent(() => import("@/components/easterEggs/TheHeart.vue")),
  meatball: defineAsyncComponent(() => import("@/components/easterEggs/TheMeatball.vue")),
  snake: defineAsyncComponent(() => import("@/components/easterEggs/TheSnake.vue")),
  superhero: defineAsyncComponent(() => import("@/components/easterEggs/TheSuperhero.vue"))
};

const easterEgg = shallowRef<Component | null>(null);

const hasSearchTrigger = (searchedWord: string, triggerSearch: string) =>
  triggerSearch.localeCompare(searchedWord, undefined, { sensitivity: "base" }) === 0;

watch(
  () => searchStore.searchedWord,
  (searchedWord: string) => {
    if (searchStore.searchState !== "exactResult") {
      return;
    }

    const sourceDirection = searchStore.searchDirection.substring(0, 2) as Language | undefined;
    if (!sourceDirection) {
      return;
    }

    for (const property in easterEggTriggerMap) {
      const easterEggKey = property as EasterEgg;

      const triggerSearches = easterEggTriggerMap[easterEggKey][sourceDirection];
      if (!triggerSearches) {
        continue;
      }

      if (!triggerSearches.some((triggerSearch) => hasSearchTrigger(searchedWord, triggerSearch))) {
        continue;
      }

      easterEgg.value = easterEggComponentMap[easterEggKey];
      return;
    }
  },
  { immediate: true }
);
</script>

<template>
  <Component :is="easterEgg" />
</template>
